import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ClipboardList, Calendar, Dumbbell, Utensils, Sparkles,
  Layers, Bot, Heart, Lock, Check, Loader2,
} from "lucide-react";
import { api } from "../lib/api";

const ICONS = {
  clipboard: ClipboardList,
  calendar: Calendar,
  dumbbell: Dumbbell,
  utensils: Utensils,
  sparkles: Sparkles,
  layers: Layers,
  bot: Bot,
  heart: Heart,
};

const AchievementTile = ({ a, index, onTap }) => {
  const Icon = ICONS[a.icon] || Sparkles;
  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 + index * 0.04 }}
      whileTap={{ scale: 0.97 }}
      onClick={() => onTap(a)}
      data-testid={`achievement-tile-${a.id}`}
      className="text-left rounded-2xl border border-zinc-800 bg-zinc-900/60 hover:border-zinc-700 p-3 flex items-center gap-3 transition-all"
    >
      <div className="relative shrink-0">
        <div
          className={`h-11 w-11 rounded-xl grid place-items-center ${a.unlocked ? "shadow-md" : ""}`}
          style={a.unlocked ? {
            background: `linear-gradient(135deg, ${a.gradient[0]}, ${a.gradient[1]})`,
            boxShadow: `0 6px 18px -6px ${a.gradient[1]}`,
          } : {
            background: "linear-gradient(135deg, #1f2937, #0f172a)",
          }}
        >
          {a.unlocked
            ? <Icon className="h-5 w-5 text-white" strokeWidth={2.2} />
            : <Lock className="h-4 w-4 text-zinc-600" strokeWidth={2.2} />}
        </div>
        {a.unlocked && (
          <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-lime-400 grid place-items-center border-2 border-zinc-900">
            <Check className="h-2.5 w-2.5 text-black" strokeWidth={3} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold leading-tight ${a.unlocked ? "text-white" : "text-zinc-400"}`}>
          {a.name}
        </div>
        <div className="text-[11px] text-zinc-500 leading-tight mt-0.5 truncate">
          {a.tag}
        </div>
        <div className="mt-2 h-1 w-full rounded-full bg-zinc-800 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${a.percent}%`,
              background: a.unlocked
                ? `linear-gradient(90deg, ${a.gradient[0]}, ${a.gradient[1]})`
                : "#52525b",
            }}
          />
        </div>
        <div className="text-[10px] text-zinc-500 mt-1 tabular-nums">
          {a.progress} / {a.threshold}
        </div>
      </div>
    </motion.button>
  );
};

export default function AchievementsGrid({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const d = await api.achievements();
        if (!cancelled) setData(d);
      } catch {}
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 flex items-center gap-2 text-sm text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading achievements…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="achievements-grid">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-base font-semibold text-white">Achievements</h3>
        <div className="text-xs text-zinc-400">
          <span className="text-white font-semibold">{data.earned_count}</span> / {data.total_count}
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Earned by using the app, not just by showing up.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {data.achievements.map((a, i) => (
          <AchievementTile key={a.id} a={a} index={i} onTap={setOpen} />
        ))}
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          onClick={() => setOpen(null)}
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm grid place-items-center px-6"
          data-testid="achievement-detail-modal"
        >
          <motion.div
            initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bg-zinc-900 border border-zinc-800 rounded-3xl p-6 text-center text-white"
          >
            <div
              className="mx-auto h-20 w-20 rounded-2xl grid place-items-center"
              style={{
                background: open.unlocked
                  ? `linear-gradient(135deg, ${open.gradient[0]}, ${open.gradient[1]})`
                  : "linear-gradient(135deg, #1f2937, #0f172a)",
                boxShadow: open.unlocked ? `0 16px 50px -12px ${open.gradient[1]}` : "none",
              }}
            >
              {(() => {
                const I = ICONS[open.icon] || Sparkles;
                return open.unlocked
                  ? <I className="h-10 w-10 text-white" strokeWidth={2.2} />
                  : <Lock className="h-8 w-8 text-zinc-600" />;
              })()}
            </div>
            <h3 className="mt-4 text-2xl font-bold">{open.name}</h3>
            <p className="mt-1 text-sm text-zinc-400">{open.tag}</p>
            <div className="mt-3 text-xs text-zinc-500 tabular-nums">
              Progress: {open.progress} / {open.threshold}
            </div>
            <button
              onClick={() => setOpen(null)}
              className="mt-5 w-full rounded-full h-11 bg-zinc-800 text-zinc-200 font-medium hover:bg-zinc-700"
              data-testid="achievement-detail-close"
            >
              Close
            </button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
